import {
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type { StampId } from '../../../engine';
import {
  bidAtAuction,
  chartOmens,
  chooseDetour,
  confess,
  finishDetour,
  fuseTwins,
  stokeFurnace,
  SUPERSTITION_INFO,
  takeDetourOffer,
  type DetourId,
  type NightRun,
  type Offer,
  type Profile,
} from '../../../campaign';
import { t } from '../../../i18n';
import { itemName, itemText } from '../../../app/describe';
import { sounds, type SoundName } from '../../../app/sound';
import { CardView } from '../CardView';
import { PawnFigure } from '../PawnFigure';
import { PendingPicker, useTaker, type Apply } from '../NightCounter';
import { DeckPicker, StampSelect, affords } from '../NightParts';
import { BrokerHand, Prop } from './Broker';
import { Emblem } from './Emblem';
import { BrassHud, NightLabel } from './Hud';
import { ItemSheet, showOffer, type ShowItem } from './Item';
import { NightIcon } from './NightIcon';
import { nightLabel } from './runText';
import { Good, VelvetTray } from './Velvet';

/**
 * The Detours (docs/LONG_NIGHT_ART_DIRECTION.md §11.15). Two Detour cards are offered face up,
 * each with its emblem, its name and one line; his hand takes away the one not chosen. Each Detour
 * is then a short scene with its own light (the light itself is the scene's, set by the screen
 * from the Detour chosen) and its own moment.
 */

const detourItem = (id: string): ShowItem => ({
  kindLabel: t('night.kind.detour'),
  name: itemName(`detour.${id}`),
  text: itemText(`detour.${id}`),
  icon: 'detour',
  object: { kind: 'fixture', emblem: `detour.${id}` },
});

/** The Fence sells at half the Superstition's price (detours.ts). */
function fencePrice(o: Offer): number {
  if (o.kind !== 'superstition') return 0;
  const price = SUPERSTITION_INFO[o.id as keyof typeof SUPERSTITION_INFO]?.price ?? 8;
  return Math.max(1, Math.floor(price / 2));
}

export function DetourScene({
  profile,
  run,
  apply,
  reduceMotion,
  onPuzzle,
}: {
  profile: Profile;
  run: NightRun;
  apply: Apply;
  reduceMotion: boolean;
  /** Opens the Puzzle Box's position to play. */
  onPuzzle: () => void;
}) {
  const d = run.detour!;
  if (!d.chosen) return <DetourChoice run={run} apply={apply} reduceMotion={reduceMotion} />;
  return (
    <ChosenDetour
      key={d.chosen}
      profile={profile}
      run={run}
      apply={apply}
      reduceMotion={reduceMotion}
      onPuzzle={onPuzzle}
    />
  );
}

/** Two doors: two Detour cards face up. The one not chosen is taken away by his hand. */
function DetourChoice({
  run,
  apply,
  reduceMotion,
}: {
  run: NightRun;
  apply: Apply;
  reduceMotion: boolean;
}) {
  const d = run.detour!;
  const [open, setOpen] = useState<DetourId | null>(null);
  const [going, setGoing] = useState<DetourId | null>(null);
  const other = going ? d.choices.findIndex((c) => c !== going) : -1;
  return (
    <div className="ln-screen dt-scene has-dock">
      <header className="nb-head">
        <NightLabel night={run.night}>
          {nightLabel(run.night)} · {t('night.afterHigh')}
        </NightLabel>
        <BrassHud brass={run.brass} reduceMotion={reduceMotion} />
      </header>
      <h1 className="sp-title">{t('night.detour')}</h1>
      <p className="sp-lead">{t('night.detourHint')}</p>
      <div className="dt-cards">
        {going && other >= 0 && (
          <BrokerHand
            pose="reach"
            at={d.choices.length > 1 ? (other === 0 ? 0.27 : 0.73) : 0.5}
            to={0.55}
            still={reduceMotion}
          />
        )}
        {d.choices.map((id, i) => (
          <button
            key={id}
            type="button"
            className={`ln-door dt-card ${going && going !== id ? 'is-taken' : ''} ${
              going === id ? 'is-chosen' : ''
            }`}
            style={{ '--d': i } as CSSProperties}
            disabled={!!going}
            onClick={() => setOpen(id)}
          >
            <Emblem name={`detour.${id}`} size={56} />
            <strong>{itemName(`detour.${id}`)}</strong>
            <span>{itemText(`detour.${id}`)}</span>
          </button>
        ))}
      </div>
      {open && (
        <ItemSheet
          item={detourItem(open)}
          actions={[
            {
              label: t('night.go'),
              primary: true,
              onClick: () => {
                const id = open;
                setOpen(null);
                if (reduceMotion) return apply((p) => chooseDetour(p, id));
                setGoing(id);
                sounds.play('paper');
                setTimeout(() => apply((p) => chooseDetour(p, id)), 1000);
              },
            },
          ]}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/** A moment that plays over the scene for a while, then lets go. */
function Moment({
  className,
  ms,
  onDone,
  children,
}: {
  className: string;
  ms: number;
  onDone: () => void;
  children?: ReactNode;
}) {
  const done = useEffectEvent(onDone);
  useEffect(() => {
    const id = setTimeout(done, ms);
    return () => clearTimeout(id);
  }, [ms]);
  return (
    <div className={`dt-moment ${className}`} aria-hidden="true">
      {children}
    </div>
  );
}

function ChosenDetour({
  profile,
  run,
  apply,
  reduceMotion,
  onPuzzle,
}: {
  profile: Profile;
  run: NightRun;
  apply: Apply;
  reduceMotion: boolean;
  onPuzzle: () => void;
}) {
  const d = run.detour!;
  const id = d.chosen!;
  const { pending, setPending, take } = useTaker(apply);
  const [cards, setCards] = useState<number[]>([]);
  const [stampCard, setStampCard] = useState<number[]>([]);
  const [stamp, setStamp] = useState<StampId | ''>('');
  const [bid, setBid] = useState(5);
  const [sealed, setSealed] = useState<number | null>(null);
  const [picks, setPicks] = useState<number[]>([]);
  const [reading, setReading] = useState<Offer | null>(null);
  const [burning, setBurning] = useState<number[]>([]);
  const [furnaceMode, setFurnaceMode] = useState<'melt' | 'stamp'>('melt');
  const [moment, setMoment] = useState<'engrave' | 'stamp' | 'merge' | 'dissolve' | null>(null);
  // The Workbench's moment plays when a Carving has just gone on a pawn.
  const [wasDone, setWasDone] = useState(d.done);
  if (d.done !== wasDone) {
    setWasDone(d.done);
    if (d.done && id === 'workbench' && !reduceMotion) setMoment('engrave');
  }

  /** Plays a moment, then does what it shows. */
  const thenApply = (m: typeof moment, ms: number, fn: Parameters<Apply>[0], sound?: SoundName) => {
    if (reduceMotion) return apply(fn);
    if (sound) sounds.play(sound);
    setMoment(m);
    setTimeout(() => {
      setMoment(null);
      apply(fn);
    }, ms);
  };

  const leave = (
    <button
      type="button"
      className={`btn btn-large ln-dock-main ${d.done ? 'btn-primary' : ''}`}
      disabled={moment !== null || burning.length > 0}
      onClick={() => apply(finishDetour)}
    >
      {d.done ? t('night.toCounter') : t('night.skipDetour')}
      <NightIcon name="arrow-right" size={18} />
    </button>
  );

  let body: ReactNode = null;
  /** The Detour's own main action, in the dock beside walking on. */
  let main: ReactNode = null;
  if (d.done) {
    body =
      id === 'auction' && d.rivals ? (
        <AuctionNotes
          rivals={d.rivals}
          yours={sealed}
          won={d.offers.length === 0}
          reduceMotion={reduceMotion}
        />
      ) : id === 'puzzle-box' ? (
        <div className={`dt-box ${d.solved ? 'is-open' : ''}`}>
          <span className="dt-box-lid" aria-hidden="true" />
          <span className="dt-box-body" aria-hidden="true" />
          <p className="ln-result">{t(d.solved ? 'night.puzzle.solved' : 'night.puzzle.failed')}</p>
        </div>
      ) : (
        <p className="ln-result dt-done">{t('night.detourDone')}</p>
      );
  } else
    switch (id) {
      case 'workbench':
      case 'lost-and-found':
      case 'fence':
        body = (
          <VelvetTray
            run={run}
            cloth={id === 'workbench' ? 'bench' : id === 'fence' ? 'wrapped' : 'box'}
            offers={d.offers.map((offer) => ({
              offer,
              ...(id === 'fence' ? { price: fencePrice(offer) } : {}),
            }))}
            actionLabel={(price) => (price ? t('night.buyFor', { n: price }) : t('night.take'))}
            onTake={(i) => {
              if (id === 'workbench') sounds.play('scratch');
              take(d.offers[i]!, (target) => (p) => takeDetourOffer(p, i, target));
            }}
          />
        );
        break;
      case 'seance':
        body = (
          <>
            {run.roster.length <= 6 && <p className="ln-sheet-note">{t('night.target.keepSix')}</p>}
            <VelvetTray
              run={run}
              cloth="smoke"
              offers={d.offers.map((offer) => ({ offer }))}
              actionLabel={() => t('night.strike')}
              onTake={(i) => {
                if (run.roster.length <= 6) return;
                setPending({
                  offer: d.offers[i]!,
                  payPawns: 1,
                  take: (target, payWith) => (p) => takeDetourOffer(p, i, target, payWith),
                });
              }}
            />
          </>
        );
        break;
      case 'furnace':
        body = (
          <div className="dt-furnace">
            <div className="dt-modes" role="group">
              {(['melt', 'stamp'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`btn btn-small ${furnaceMode === m ? 'is-on' : ''}`}
                  aria-pressed={furnaceMode === m}
                  disabled={burning.length > 0 || moment !== null}
                  onClick={() => setFurnaceMode(m)}
                >
                  {t(m === 'melt' ? 'night.furnace.melt' : 'night.furnace.stamp')}
                </button>
              ))}
            </div>
            {furnaceMode === 'melt' ? (
              <DeckPicker
                deck={run.deck}
                max={3}
                selected={cards}
                onChange={setCards}
                burning={burning}
              />
            ) : (
              <>
                <DeckPicker deck={run.deck} max={1} selected={stampCard} onChange={setStampCard} />
                <StampSelect value={stamp} onChange={setStamp} />
              </>
            )}
          </div>
        );
        main =
          furnaceMode === 'melt' ? (
            <button
              type="button"
              className="btn btn-danger btn-large ln-dock-main"
              disabled={cards.length === 0 || burning.length > 0}
              onClick={() => {
                const melt = cards;
                if (reduceMotion) return apply((p) => stokeFurnace(p, melt));
                sounds.play('ember');
                setBurning(melt);
                setTimeout(() => {
                  setBurning([]);
                  setCards([]);
                  apply((p) => stokeFurnace(p, melt));
                }, 1300);
              }}
            >
              {t('night.furnace.meltButton', { n: cards.length })}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-large ln-dock-main"
              disabled={stampCard.length !== 1 || !stamp || moment !== null}
              onClick={() => {
                const card = stampCard[0]!;
                const st = stamp as StampId;
                thenApply('stamp', 700, (p) => stokeFurnace(p, [], { card, stamp: st }), 'stamp');
              }}
            >
              {t('night.furnace.stampButton')}
            </button>
          );
        break;
      case 'twins': {
        const [a, b] = cards.map((i) => run.deck[i]);
        body = (
          <>
            {/* A mirror down the middle: the two cards face each other across it. */}
            <div className={`dt-mirror ${moment === 'merge' ? 'is-merging' : ''}`}>
              {[a, b].map((c, i) => (
                <span key={i} className={`dt-twin is-${i === 0 ? 'left' : 'right'}`}>
                  {c ? (
                    <CardView
                      size="md"
                      face={c.kind === 'normal' ? c.face : null}
                      wild={c.kind === 'wild'}
                      stamp={c.stamp}
                      finish={c.finish}
                      label=""
                    />
                  ) : (
                    <span className="dt-twin-empty" />
                  )}
                </span>
              ))}
            </div>
            <p className="ln-hint">{t('night.twins.pick')}</p>
            <DeckPicker deck={run.deck} max={2} selected={cards} onChange={setCards} />
          </>
        );
        main = (
          <button
            type="button"
            className="btn btn-primary btn-large ln-dock-main"
            disabled={cards.length !== 2 || moment !== null}
            onClick={() => {
              const [x, y] = cards as [number, number];
              thenApply('merge', 1000, (p) => fuseTwins(p, x, y), 'glass');
            }}
          >
            {t('night.twins.fuse')}
          </button>
        );
        break;
      }
      case 'auction': {
        const o = d.offers[0];
        body = o ? (
          <>
            <div className="dt-lot">
              <Good offer={o} size={72} />
              <strong>{showOffer(o).name}</strong>
            </div>
            <div className="ln-bid">
              <label className="field">
                <span className="field-label">{t('night.auction.bid')}</span>
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={bid}
                  onChange={(e) => setBid(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                />
              </label>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!affords(run, bid)}
                onClick={() => {
                  setSealed(bid);
                  sounds.play('wax');
                  apply((p) => bidAtAuction(p, bid));
                }}
              >
                {t('night.auction.seal', { n: bid })}
              </button>
            </div>
          </>
        ) : (
          <p className="ln-empty">{t('night.auction.empty')}</p>
        );
        break;
      }
      case 'confessional':
        body = (
          <div className="ln-choice is-stacked">
            <button
              type="button"
              className="btn"
              disabled={moment !== null}
              onClick={() => thenApply('dissolve', 1200, (p) => confess(p, false), 'breath')}
            >
              {t('night.confess.plain')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!affords(run, 8) || moment !== null}
              onClick={() => thenApply('dissolve', 1200, (p) => confess(p, true), 'breath')}
            >
              {t('night.confess.rot')}
            </button>
          </div>
        );
        break;
      case 'cartographer':
        body = (
          <>
            <p className="ln-hint">{t('night.chart.hint')}</p>
            {/* The Omens on offer, each with what it does; the three chosen are pinned in brass. */}
            <ul className="dt-chart">
              {d.offers.map((o, i) => (
                <ChartOmen
                  key={i}
                  offer={o}
                  on={picks.includes(i)}
                  full={picks.length >= 3}
                  onToggle={() =>
                    setPicks((cur) =>
                      cur.includes(i)
                        ? cur.filter((x) => x !== i)
                        : cur.length < 3
                          ? [...cur, i]
                          : cur,
                    )
                  }
                  onRead={() => setReading(o)}
                />
              ))}
            </ul>
            {reading && (
              <ItemSheet
                item={{
                  ...showOffer(reading),
                  ...(reading.kind === 'omen'
                    ? { text: `${itemText(`omen.${reading.id}`)}\n\n${t('night.chart.where')}` }
                    : {}),
                }}
                actions={[]}
                onClose={() => setReading(null)}
              />
            )}
          </>
        );
        main = (
          <button
            type="button"
            className="btn btn-primary btn-large ln-dock-main"
            disabled={picks.length !== 3}
            onClick={() => {
              sounds.play('place');
              apply((p) => chartOmens(p, picks));
            }}
          >
            {t('night.chart.button', { n: picks.length })}
          </button>
        );
        break;
      case 'puzzle-box':
        body = (
          <div className="dt-box">
            <span className="dt-box-lid" aria-hidden="true" />
            <span className="dt-box-body" aria-hidden="true" />
            <p className="ln-result">{t('night.puzzle.target', { n: d.puzzle?.target ?? 0 })}</p>
            <button type="button" className="btn btn-primary btn-large" onClick={onPuzzle}>
              {t('night.puzzle.open')}
            </button>
          </div>
        );
        break;
    }

  return (
    <div className={`ln-screen dt-scene dt-${id} has-dock`}>
      <header className="nb-head">
        <NightLabel night={run.night}>{t('night.kind.detour')}</NightLabel>
        <BrassHud brass={run.brass} reduceMotion={reduceMotion} />
      </header>
      <Setting id={id} />
      <h1 className="sp-title">{itemName(`detour.${id}`)}</h1>
      <p className="sp-lead">{itemText(`detour.${id}`)}</p>
      {body}
      <div className="ln-dock">
        {main ? (
          <>
            <button
              type="button"
              className="btn btn-large"
              disabled={moment !== null || burning.length > 0}
              onClick={() => apply(finishDetour)}
            >
              {t('night.walkOn')}
            </button>
            {main}
          </>
        ) : (
          leave
        )}
      </div>
      {moment === 'engrave' && (
        <Moment className="is-engrave" ms={1400} onDone={() => setMoment(null)}>
          <span className="dt-engrave">
            <PawnFigure side="S" crowned={false} immune={false} inscription={null} />
            <span className="dt-lines" />
          </span>
        </Moment>
      )}
      {moment === 'stamp' && (
        <Moment className="is-stamp" ms={900} onDone={() => undefined}>
          <span className="dt-stamp" />
        </Moment>
      )}
      {moment === 'dissolve' && (
        <Moment className="is-dissolve" ms={1400} onDone={() => undefined}>
          <span className="dt-ink" />
          <span className="dt-ink is-2" />
          <span className="dt-ink is-3" />
        </Moment>
      )}
      <PendingPicker
        profile={profile}
        run={run}
        pending={pending}
        apply={apply}
        onClose={() => setPending(null)}
      />
    </div>
  );
}

/** Each Detour's setting, drawn small above its name. */
function Setting({ id }: { id: DetourId }) {
  if (id === 'auction')
    // The three regulars, as silhouettes, each with their prop.
    return (
      <div className="dt-setting is-auction" aria-hidden="true">
        {[0, 1, 2].map((r) => (
          <span key={r} className="dt-regular">
            <span className="dt-head" />
            <Prop name={`regular.${r}`} size={26} />
          </span>
        ))}
      </div>
    );
  return (
    <div className={`dt-setting is-${id}`} aria-hidden="true">
      <Emblem name={`detour.${id}`} size={52} />
      {id === 'fence' && (
        // A black feather left on the counter.
        <svg className="dt-feather" viewBox="0 0 40 14" width="40" height="14">
          <path
            d="M2 12 C10 10 22 3 38 2 C30 6 22 10 6 12 Z"
            fill="#0a0810"
            stroke="rgba(184,166,255,0.45)"
            strokeWidth="0.6"
          />
          <path d="M2 12 C14 8 26 5 37 2.4" stroke="rgba(184,166,255,0.35)" strokeWidth="0.5" />
        </svg>
      )}
    </div>
  );
}

/**
 * One Omen the Cartographer offers: its emblem, its name and what it does, on one row. A tap
 * pins it (three at most); holding it opens it in full.
 */
function ChartOmen({
  offer,
  on,
  full,
  onToggle,
  onRead,
}: {
  offer: Offer;
  on: boolean;
  /** Three are pinned already: the others wait. */
  full: boolean;
  onToggle: () => void;
  onRead: () => void;
}) {
  const shown = showOffer(offer);
  const hold = useRef<ReturnType<typeof setTimeout> | null>(null);
  const held = useRef(false);
  const release = () => {
    if (hold.current) clearTimeout(hold.current);
    hold.current = null;
  };
  return (
    <li>
      <button
        type="button"
        className={`dt-omen ${on ? 'is-on' : ''} ${full && !on ? 'is-waiting' : ''}`}
        aria-pressed={on}
        onPointerDown={() => {
          held.current = false;
          release();
          hold.current = setTimeout(() => {
            held.current = true;
            onRead();
          }, 450);
        }}
        onPointerUp={release}
        onPointerLeave={release}
        onPointerCancel={release}
        onContextMenu={(e) => e.preventDefault()}
        onClick={() => {
          if (held.current) {
            held.current = false;
            return;
          }
          onToggle();
        }}
      >
        <span className="dt-omen-art" aria-hidden="true">
          <Emblem name={offer.kind === 'omen' ? `omen.${offer.id}` : 'lot.omen'} size={40} />
        </span>
        <span className="dt-omen-text">
          <strong>{shown.name}</strong>
          <span>{offer.kind === 'omen' ? itemText(`omen.${offer.id}`) : shown.text}</span>
        </span>
        <span className="dt-omen-pin" aria-hidden="true" />
      </button>
    </li>
  );
}

/**
 * The Auction's end: the sealed bids as folded notes, opened one at a time with a hush between
 * them, yours first.
 */
function AuctionNotes({
  rivals,
  yours,
  won,
  reduceMotion,
}: {
  rivals: readonly number[];
  yours: number | null;
  won: boolean;
  reduceMotion: boolean;
}) {
  const notes = [...(yours !== null ? [yours] : []), ...rivals];
  const [opened, setOpened] = useState(reduceMotion ? notes.length : 0);
  useEffect(() => {
    if (reduceMotion) return;
    const timers = notes.map((_, i) =>
      setTimeout(
        () => {
          setOpened(i + 1);
          sounds.play('paper');
        },
        500 + i * 900,
      ),
    );
    return () => timers.forEach(clearTimeout);
    // The notes are fixed once the bids are in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion]);
  return (
    <>
      <div className="dt-notes">
        {notes.map((n, i) => (
          <span
            key={i}
            className={`dt-note ${i < opened ? 'is-open' : ''} ${
              yours !== null && i === 0 ? 'is-yours' : ''
            }`}
          >
            <span className="dt-note-who">
              {yours !== null && i === 0 ? (
                t('match.you')
              ) : (
                <Prop name={`regular.${i - (yours !== null ? 1 : 0)}`} size={18} />
              )}
            </span>
            <span className="dt-note-n">¤{n}</span>
          </span>
        ))}
      </div>
      {opened >= notes.length && (
        <p className="ln-result">
          {t(won ? 'night.auction.won' : 'night.auction.lost', { bids: rivals.join(', ') })}
        </p>
      )}
    </>
  );
}
